import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addShoppingCartItems } from "../../../../api/action/user.jsx";
import FormatPrice from "../../../../components/FormatPrice.jsx";
import { useCart } from "../../../../context/CartContext.jsx";

const ProdDetailsContext = createContext(null);
export const ProdDetailsProvider = ProdDetailsContext.Provider;
export const useProdDetails = () => useContext(ProdDetailsContext);

const TFProductDetails = () => {
    const navigate = useNavigate();
    const userDetail = useSelector((state) => state?.auth?.data);
    const storedProduct = useSelector((state) => state.selectedProduct.selectedProduct);
    const existingProducts = JSON.parse(localStorage.getItem("cartProducts"));
    const { addToCart } = useCart();
    const [productDetails, setProductDetails] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [alertMsg, setAlertMsg] = useState({ open: false, message: "", severity: "error" });

    useEffect(() => {
        setProductDetails(storedProduct);
        return () => setProductDetails([]);
    }, [storedProduct]);

    const addToShoppingCart = async (product) => {
        const shoppingCartId = userDetail?.account?.shoppingCart?.primaryId;
        if (!shoppingCartId) {
            setAlertMsg({ message: "Shopping cart ID is missing. Refresh the page.", open: true, severity: "error" });
            return;
        }

        try {
            const response = await addShoppingCartItems(shoppingCartId, product);
            if (response) navigate("/cart");
        } catch (error) {
            setAlertMsg({ message: error.message, open: true, severity: "error" });
        }
    };

    const handleCart = async (productDetails) => {
        let isProductInCart = (existingProducts || []).some((item) => item.product.primaryId === productDetails.primaryId);
        if (!isProductInCart) {
            let retailReq = {
                quantity: quantity,
                product: {
                    deleted: productDetails.deleted,
                    name: productDetails.name,
                    code: productDetails.code,
                    _id: productDetails._id,
                    primaryId: productDetails.primaryId,
                    bulkOrderEnabled: Boolean(productDetails.bulkOrderEnabled),
                },
            };
            addToShoppingCart(retailReq);
            addToCart(retailReq);
        } else {
            setAlertMsg({ message: "Product already in the cart", open: true, severity: "error" });
        }
    };

    const incrementQuantity = () => setQuantity((prev) => Math.min(prev + 1, 10));
    const decrementQuantity = () => setQuantity((prev) => Math.max(prev - 1, 1));

    const [mainImage, setMainImage] = useState("");

    useEffect(() => {
        if (productDetails?.imageUrlsList?.length) setMainImage(productDetails.imageUrlsList[0]);
    }, [productDetails]);

    return (
        <div style={{ paddingTop: "10rem" }}>

            <div className="mx-auto max-w-6xl px-4 py-10 bg-gray-50">
                <div className="mb-6 flex flex-wrap gap-2 overflow-x-auto">
                    {productDetails?.imageUrlsList?.map((url, index) => (
                        <img
                            key={index}
                            src={url}
                            alt={`${productDetails?.name || "Product"} thumbnail ${index + 1}`}
                            className="h-16 w-16 object-cover rounded-md border border-gray-300 shadow-md cursor-pointer hover:scale-105 transition"
                            onMouseEnter={() => setMainImage(url)}
                        />
                    ))}
                </div>

                <div className="flex flex-col lg:flex-row gap-10">
                    {/* Main Product Image */}
                    <div className="w-full lg:w-2/3">
                        <div className="relative overflow-hidden rounded-lg border border-gray-300 shadow-lg">
                            <img src={mainImage} alt={productDetails?.name} className="w-full h-[350px] object-contain" />
                        </div>
                    </div>

                    {/* Product Info */}
                    <div className="flex flex-col lg:w-1/3">
                        <h2 className="text-xl font-semibold text-gray-800 text-center">{productDetails?.name}</h2>
                        <p className="mt-2 text-center text-teal-600 text-xl font-bold">
                            {productDetails.price && (
                                <FormatPrice code={productDetails.price.currency || "USD"} price={productDetails.price.priceAmt} />
                            )}
                        </p>

                        {/* Quantity Selector */}
                        <div className="flex items-center justify-between mt-6 border-b border-gray-300 pb-4">
                            <div className="flex h-10 items-center rounded-md border border-gray-300 shadow-sm">
                                <button
                                    className="px-3 py-1 text-gray-600 hover:bg-gray-200 transition"
                                    disabled={quantity === 1}
                                    onClick={decrementQuantity}
                                >
                                    -
                                </button>
                                <span className="px-4 text-lg font-semibold text-gray-700">{quantity}</span>
                                <button
                                    className="px-3 py-1 text-gray-600 hover:bg-gray-200 transition"
                                    onClick={incrementQuantity}
                                >
                                    +
                                </button>
                            </div>
                            <button
                                onClick={() => handleCart(productDetails)}
                                className="px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-md shadow-md hover:bg-teal-700 transition"
                            >
                                Add to cart
                            </button>
                        </div>

                        {/* Product Description */}
                        <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Description:</h3>
                            <p className="text-gray-700">{productDetails?.description}</p>
                        </div>
                    </div>
                </div>

                {/* Snackbar for Alerts */}
                {alertMsg.open && (
                    <div className="fixed top-5 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white px-6 py-3 rounded-md shadow-lg">
                        {alertMsg.message}
                    </div>
                )}
            </div>
        </div>

    );
};

export default TFProductDetails;
